import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerModal from '../components/CustomerModal';
import CustomerMergeModal from '../components/CustomerMergeModal';
import { useAuth } from '../App';
import { useToast } from '../hooks/useToast';
import { ToastContainer } from '../components/ToastContainer';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function Customers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');
  const [showingRecent, setShowingRecent] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { toasts, success, error: toastError } = useToast();

  // Build URL with showDeleted param for all customer fetches
  const buildUrl = (search, limitVal) => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    else if (limitVal) params.set('limit', limitVal);
    return `${API_URL}/customers?${params.toString()}`;
  };

  const loadRecentCustomers = async () => {
    setLoading(true);
    try {
      const res = await fetch(buildUrl(null, 50), {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to fetch customers');
      const data = await res.json();
      setCustomers(data.customers);
      setShowingRecent(true);
      setHasLoaded(true);
    } catch (err) {
      toastError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const url = searchQuery
        ? buildUrl(searchQuery)
        : buildUrl(null, 50);

      const res = await fetch(url, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to fetch customers');
      const data = await res.json();
      setCustomers(data.customers);
    } catch (err) {
      toastError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery.length >= 3) {
        setShowingRecent(false);
        fetchCustomers();
      } else if (searchQuery.length > 0 && searchQuery.length < 3) {
        // Partial search - clear results but don't reload recent yet
        setCustomers([]);
        setShowingRecent(false);
      } else {
        // Search cleared - just clear the filter state, don't auto-load
        setCustomers([]);
        setShowingRecent(false);
        setHasLoaded(false);
      }
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);


  const openCreateModal = () => {
    setEditingCustomer(null);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setError('');
    setShowModal(true);
  };

  const handleModalSuccess = (customer) => {
    success(editingCustomer ? 'Customer updated successfully' : 'Customer created successfully');
    setShowModal(false);
    setEditingCustomer(null);
    fetchCustomers();
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingCustomer(null);
    fetchCustomers(); // Refresh based on current filter
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;

    try {
      const res = await fetch(`${API_URL}/customers/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to delete customer');
      }

      success('Customer deleted successfully');
      fetchCustomers();
    } catch (err) {
      toastError(err.message);
    }
  };

  return (
    <div className="h-full flex flex-col px-4 py-1 sm:px-0">
      <ToastContainer toasts={toasts} />
      <div className="w-full bg-white shadow rounded-lg flex flex-col flex-1 overflow-hidden">
        <div className="flex-shrink-0 p-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">Customer Management</h1>
            <div className="flex gap-2">
              <button
                onClick={loadRecentCustomers}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                Load Recent Customers
              </button>
              {user?.role === 'admin' && (
                <button
                  onClick={() => setShowMergeModal(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                >
                  Merge Customers
                </button>
              )}
              <button
                onClick={openCreateModal}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
              >
                Add Customer
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div>
            <input
              type="text"
              placeholder="Search customers... Use multiple terms to narrow results (e.g., '457 Mart' matches phone and name)"
              autoComplete="off"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>
        </div>

        {loading && (
          <div className="flex-shrink-0 text-center py-4">
            <p>Loading...</p>
          </div>
        )}

        {!loading && showingRecent && (
          <div className="flex-shrink-0 bg-blue-50 border-b border-blue-200 px-6 py-2">
            <p className="text-sm text-blue-700 font-medium">Showing {customers.length} most recent customers</p>
          </div>
        )}

        {!loading && (
          <div className="flex-1 overflow-auto px-6 pb-6">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">City, State</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opt-ins</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {customers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                      No results, please enter search terms.
                    </td>
                  </tr>
                ) : (
                  customers.map((customer) => (
                    <tr
                      key={customer.id}
                      onClick={() => navigate(`/customers/${customer.id}/dashboard`)}
                      className="hover:bg-gray-50 cursor-pointer"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {customer.first_name} {customer.last_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {customer.email || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {customer.mobile_phone || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {customer.city && customer.state ? `${customer.city}, ${customer.state}` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex gap-1">
                          {customer.opt_in_text_messages && (
                            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">SMS</span>
                          )}
                          {customer.opt_in_phone_messages && (
                            <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">Phone</span>
                          )}
                          {!customer.opt_in_text_messages && !customer.opt_in_phone_messages && (
                            <span>-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(customer.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Customer Create/Edit Modal */}
      <CustomerModal
        showModal={showModal}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        editingCustomer={editingCustomer}
        initialData={editingCustomer ? {
          first_name: editingCustomer.first_name || '',
          last_name: editingCustomer.last_name || '',
          mobile_phone: editingCustomer.mobile_phone || '',
          home_phone: editingCustomer.home_phone || '',
          work_phone: editingCustomer.work_phone || '',
          email: editingCustomer.email || '',
          opt_in_text_messages: editingCustomer.opt_in_text_messages || false,
          opt_in_phone_messages: editingCustomer.opt_in_phone_messages || false,
          address_line1: editingCustomer.address_line1 || '',
          address_line2: editingCustomer.address_line2 || '',
          city: editingCustomer.city || '',
          state: editingCustomer.state || '',
          postal_code: editingCustomer.postal_code || '',
          notes: editingCustomer.notes || ''
        } : {}}
      />

      <CustomerMergeModal
        showModal={showMergeModal}
        onClose={() => setShowMergeModal(false)}
        onSuccess={() => {
          setShowMergeModal(false);
          fetchCustomers();
        }}
      />
    </div>
  );
}